import {ExternalLayerRow} from './ExternalLayerData';

/**
 * Odhad datového typu sloupců externí (připojené / importované) vrstvy.
 *
 * Externí vrstvy nemají žádnou metadatovou definici atributů – GeoJSON properties přicházejí
 * typicky jako text (např. z SHP/CSV importu), takže by se čísla řadila jako řetězce ("10" < "9")
 * a filtr by nabízel jen fulltext. Typ proto odhadujeme z hodnot: pokud VŠECHNY vyplněné hodnoty
 * sloupce odpovídají jednomu vzoru, sloupec ten typ dostane, jinak zůstává textem.
 *
 * Odhad je záměrně konzervativní – v nejasných případech vrací 'text', aby se nikdy neztratila
 * ani nezkomolila původní hodnota.
 */

/** Odhadnutý typ sloupce – odpovídá typům sloupce/filtru v FinalTable. */
export type ExternalColumnType = 'text' | 'integer' | 'decimal' | 'date' | 'dateTime' | 'boolean';

/** Kolik řádků maximálně vzorkujeme při odhadu (kvůli výkonu u velkých importů). */
const SAMPLE_SIZE = 2000;

/** Celé číslo bez vodicích nul (vodicí nula = kód typu PSČ/IČO, ten musí zůstat textem). */
const INTEGER_RE = /^[+-]?(0|[1-9]\d{0,14})$/;
/** Desetinné číslo s tečkou i čárkou jako oddělovačem. */
const DECIMAL_RE = /^[+-]?(0|[1-9]\d{0,14})[.,]\d{1,10}$/;
/** ISO datum – 2024-05-01 */
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
/** ISO datum a čas – 2024-05-01T10:30(:00)(.000)(Z|+02:00), mezera místo T povolena */
const ISO_DATE_TIME_RE = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2})?(\.\d+)?(Z|[+-]\d{2}:?\d{2})?$/;
/** České datum – 1.5.2024 / 01. 05. 2024 */
const CZ_DATE_RE = /^(\d{1,2})\.\s*(\d{1,2})\.\s*(\d{4})$/;
/** České datum a čas – 1.5.2024 10:30(:00) */
const CZ_DATE_TIME_RE = /^(\d{1,2})\.\s*(\d{1,2})\.\s*(\d{4})[ T](\d{1,2}):(\d{2})(:(\d{2}))?$/;

const TRUE_VALUES = new Set(['true', 'ano', 'yes']);
const FALSE_VALUES = new Set(['false', 'ne', 'no']);

/** Hodnota, která se do odhadu nepočítá (prázdná buňka). */
const isEmptyValue = (value: unknown): boolean => value === null || value === undefined || (typeof value === 'string' && value.trim() === '');

/** Typ jedné hodnoty; `undefined` = hodnotu neumíme zařadit (→ celý sloupec bude text). */
const detectValueType = (value: unknown): ExternalColumnType | undefined => {
    if (typeof value === 'boolean') return 'boolean';
    if (typeof value === 'number') return Number.isInteger(value) ? 'integer' : 'decimal';
    if (typeof value !== 'string') return undefined; // objekty (číselníky, vnořená data) neřešíme

    const text = value.trim();
    const lower = text.toLowerCase();

    if (TRUE_VALUES.has(lower) || FALSE_VALUES.has(lower)) return 'boolean';
    if (INTEGER_RE.test(text)) return 'integer';
    if (DECIMAL_RE.test(text)) return 'decimal';
    if (ISO_DATE_TIME_RE.test(text) || CZ_DATE_TIME_RE.test(text)) return 'dateTime';
    if (ISO_DATE_RE.test(text) || CZ_DATE_RE.test(text)) return 'date';

    return undefined;
};

/**
 * Sloučí typ dosud zjištěný pro sloupec s typem další hodnoty.
 * Kombinovat lze jen příbuzné typy (integer+decimal → decimal, date+dateTime → dateTime),
 * jakýkoli jiný rozpor znamená textový sloupec.
 */
const mergeTypes = (a: ExternalColumnType, b: ExternalColumnType): ExternalColumnType => {
    if (a === b) return a;
    if ((a === 'integer' && b === 'decimal') || (a === 'decimal' && b === 'integer')) return 'decimal';
    if ((a === 'date' && b === 'dateTime') || (a === 'dateTime' && b === 'date')) return 'dateTime';
    return 'text';
};

/** Odhadne typ jednoho sloupce ze vzorku hodnot řádků. */
const inferColumnType = (rows: ExternalLayerRow[], key: string): ExternalColumnType => {
    let result: ExternalColumnType | undefined;
    let count = 0;

    for (const row of rows) {
        const value = row[key];
        if (isEmptyValue(value)) continue;

        const valueType = detectValueType(value);
        if (!valueType) return 'text';

        result = result ? mergeTypes(result, valueType) : valueType;
        if (result === 'text') return 'text';

        if (++count >= SAMPLE_SIZE) break;
    }

    // Sloupec bez jediné vyplněné hodnoty necháme jako text (není z čeho odhadovat).
    return result ?? 'text';
};

/** Odhadne typy všech sloupců. */
export const inferExternalLayerColumnTypes = (rows: ExternalLayerRow[], columnKeys: string[]): Record<string, ExternalColumnType> => {
    const result: Record<string, ExternalColumnType> = {};
    columnKeys.forEach(key => {
        result[key] = inferColumnType(rows, key);
    });
    return result;
};

const pad = (value: number, length = 2): string => String(value).padStart(length, '0');

/**
 * Převede datumovou hodnotu na ISO řetězec.
 * ISO tvar se ponechává (jen se sjednotí oddělovač), český tvar se přeskládá.
 * FinalTable u datumových sloupců počítá s ISO řetězcem (parseISO), proto NEvracíme Date.
 */
const toIsoDateString = (text: string, withTime: boolean): string | null => {
    const czDateTime = CZ_DATE_TIME_RE.exec(text);
    if (czDateTime) {
        const [, day, month, year, hours, minutes, , seconds] = czDateTime;
        return `${year}-${pad(Number(month))}-${pad(Number(day))}T${pad(Number(hours))}:${minutes}:${seconds ?? '00'}`;
    }

    const czDate = CZ_DATE_RE.exec(text);
    if (czDate) {
        const [, day, month, year] = czDate;
        const date = `${year}-${pad(Number(month))}-${pad(Number(day))}`;
        return withTime ? `${date}T00:00:00` : date;
    }

    if (ISO_DATE_TIME_RE.test(text)) return text.replace(' ', 'T');
    if (ISO_DATE_RE.test(text)) return withTime ? `${text}T00:00:00` : text;

    return null;
};

/** Převede jednu hodnotu na odhadnutý typ; při neúspěchu vrátí původní hodnotu. */
const coerceValue = (value: unknown, type: ExternalColumnType): unknown => {
    if (isEmptyValue(value)) return null;

    switch (type) {
        case 'integer':
        case 'decimal': {
            if (typeof value === 'number') return value;
            const numeric = Number(String(value).trim().replace(',', '.'));
            return Number.isNaN(numeric) ? value : numeric;
        }
        case 'date':
        case 'dateTime': {
            if (typeof value !== 'string') return value;
            return toIsoDateString(value.trim(), type === 'dateTime') ?? value;
        }
        case 'boolean': {
            if (typeof value === 'boolean') return value;
            const lower = String(value).trim().toLowerCase();
            if (TRUE_VALUES.has(lower)) return true;
            if (FALSE_VALUES.has(lower)) return false;
            return value;
        }
        default:
            return value;
    }
};

/**
 * Zjistí, na kolik desetinných míst je potřeba sloupec zobrazovat (max. napříč hodnotami).
 * Bez toho by FinalTable u desetinných čísel zaokrouhlovala na výchozí počet míst a např.
 * souřadnice by se zobrazily zkomoleně. Omezeno na 10 míst.
 */
export const getMaxFractionDigits = (rows: ExternalLayerRow[], key: string): number => {
    let max = 0;
    for (const row of rows) {
        const value = row[key];
        if (typeof value !== 'number' || Number.isInteger(value)) continue;
        const decimals = String(value).split('.')[1]?.length ?? 0;
        if (decimals > max) max = Math.min(decimals, 10);
        if (max === 10) break;
    }
    return max;
};

/**
 * Vrátí řádky s hodnotami převedenými na odhadnuté typy.
 *
 * Bez převodu by odhad typu nedával smysl – klientská tabulka řadí i filtruje podle skutečného
 * typu hodnoty v datech (číslo porovnává číselně, text přes localeCompare/fulltext).
 * Textové sloupce se nekopírují zbytečně – pokud není co převádět, vrací se původní pole.
 */
export const coerceExternalLayerRows = (rows: ExternalLayerRow[], columnTypes: Record<string, ExternalColumnType>): ExternalLayerRow[] => {
    const typedKeys = Object.keys(columnTypes).filter(key => columnTypes[key] !== 'text');
    if (typedKeys.length === 0) return rows;

    return rows.map(row => {
        const result: ExternalLayerRow = {...row};
        typedKeys.forEach(key => {
            if (key in row) result[key] = coerceValue(row[key], columnTypes[key]);
        });
        return result;
    });
};
